import type { Order } from "@/lib/projects";
import { getOrder } from "@/lib/projectOverview";
import { listEmployeeTimes, type EmployeeTimeEntry } from "@/lib/employeeTimes";
import { isBookableForHours } from "@/lib/material";
import { formatHours, parseCustomHoursValue } from "@/pages/projekte/hoursUsage";
import {
  getHeroTimes,
  getSubcontractorTimes,
  type HeroTimeEntry,
  type SubcontractorTimeEntry,
} from "@/features/timesheetFlow/timesheetFlowApi";
import { getHeroPartnerName } from "@/features/timesheetFlow/lib/heroTimesAggregation";
import { listHeroStundenZuordnung, mergeHeroTimeItems } from "@/lib/stundenUmbuchung";

export type BookedHoursSource = "hero" | "subcontractor" | "employee";

export interface BookedHoursRow {
  id: string;
  person: string;
  /** ISO-8601 oder Datumsstring für Sortierung und Anzeige */
  when: string;
  whenSort: number;
  hours: number;
  source: BookedHoursSource;
  sourceLabel: string;
  comment?: string | null;
  /** App-Zeiterfassung: Datensatz, dessen Angebotsposition hier geändert werden kann. */
  employeeTimeId?: number | null;
  positionId?: number | null;
  /** „Pos. 3 – Bezeichnung“, sonst null. */
  positionLabel?: string | null;
  positionPreisart?: "festpreis" | "nachweis" | null;
  /** Gesetzt, wenn die Stunden schon auf einem Stundennachweis liegen. */
  timesheetLabel?: string | null;
  /** Gesetzt, wenn die Position hier nicht geändert werden kann. */
  positionEditBlock?: string | null;
}

export interface BookedHoursDetail {
  totalHours: number;
  rows: BookedHoursRow[];
  heroError: boolean;
  subcontractorError: boolean;
  employeeError?: boolean;
}

function isPauseEntry(entry: HeroTimeEntry): boolean {
  return /pause/i.test(String(entry.category_name || ""));
}

function heroEntryHours(entry: HeroTimeEntry): number {
  const seconds = entry.duration_in_seconds;
  if (typeof seconds !== "number" || !Number.isFinite(seconds) || seconds <= 0) return 0;
  return seconds / 3600;
}

function subcontractorPerson(entry: SubcontractorTimeEntry): string {
  return (
    String(entry.user_display || entry.person_name || entry.user_login || "").trim() || "Unbekannt"
  );
}

function parseWhenSort(value: string | null | undefined): number {
  if (!value) return 0;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? 0 : d.getTime();
}

export function formatBookedPositionLabel(
  positionNr: number | null | undefined,
  bezeichnung: string | null | undefined,
): string | null {
  const name = String(bezeichnung || "").trim();
  if (positionNr == null && !name) return null;
  const nr = positionNr != null ? `Pos. ${positionNr}` : "Pos.";
  return name ? `${nr} – ${name}` : nr;
}

export interface EmployeeBookedPositionMeta {
  employeeTimeId: number;
  positionId: number | null;
  positionLabel: string | null;
  positionPreisart: "festpreis" | "nachweis" | null;
  timesheetLabel: string | null;
  positionEditBlock: string | null;
}

export function employeeBookedPositionMeta(entry: EmployeeTimeEntry): EmployeeBookedPositionMeta {
  const positionIdRaw = entry.geschaeftsdokument_position_id;
  const positionId =
    positionIdRaw != null && Number.isFinite(Number(positionIdRaw)) ? Number(positionIdRaw) : null;
  const positionLabel =
    positionId != null
      ? formatBookedPositionLabel(entry.position_nr, entry.position_bezeichnung) ?? "Position"
      : null;
  const preisart = String(entry.position_preisart || "").trim().toLowerCase();
  const positionPreisart =
    positionId == null
      ? null
      : preisart === "nachweis"
        ? "nachweis"
        : preisart === "festpreis"
          ? "festpreis"
          : null;
  const displayNumber = String(entry.timesheet_display_number || "").trim();
  const claimed = Number(entry.claimed_hours_decimal);
  const onTimesheet =
    entry.timesheet_id != null || (Number.isFinite(claimed) && claimed > 0);
  const timesheetLabel = !onTimesheet
    ? null
    : displayNumber
      ? `Stundennachweis ${displayNumber}`
      : "einem Stundennachweis";
  const positionEditBlock = isBookableForHours(entry)
    ? null
    : "Das Projekt ist für Stundenbuchungen gesperrt.";
  return {
    employeeTimeId: entry.id,
    positionId,
    positionLabel,
    positionPreisart,
    timesheetLabel,
    positionEditBlock,
  };
}

function buildHeroRows(items: HeroTimeEntry[]): BookedHoursRow[] {
  const rows: BookedHoursRow[] = [];
  for (const entry of items) {
    if (isPauseEntry(entry)) continue;
    if (!entry.start) continue;
    const hours = heroEntryHours(entry);
    if (hours <= 0) continue;
    const uuid = String(entry._uuid || entry.uuid || entry.hero_uuid || entry.row_key || entry.start);
    rows.push({
      id: `hero:${uuid}`,
      person: getHeroPartnerName(entry),
      when: entry.start,
      whenSort: parseWhenSort(entry.start),
      hours,
      source: "hero",
      sourceLabel: "Hero",
      comment: entry.comment ?? null,
    });
  }
  return rows;
}

function buildSubcontractorRows(items: SubcontractorTimeEntry[]): BookedHoursRow[] {
  const rows: BookedHoursRow[] = [];
  for (const entry of items) {
    const hours = parseCustomHoursValue(entry.hours_decimal);
    if (hours == null || hours <= 0) continue;
    const workDate = String(entry.work_date || "").trim();
    rows.push({
      id: `sub:${entry.id}`,
      person: subcontractorPerson(entry),
      when: workDate || "—",
      whenSort: parseWhenSort(workDate ? `${workDate}T12:00:00` : null),
      hours,
      source: "subcontractor",
      sourceLabel: "Nachunternehmer",
      comment: entry.comment ?? null,
    });
  }
  return rows;
}

/** Eigene ZE – Pausen und Hero-Spiegelungen auslassen (Hero kommt separat). */
function buildEmployeeRows(items: EmployeeTimeEntry[]): BookedHoursRow[] {
  const rows: BookedHoursRow[] = [];
  for (const entry of items) {
    if (entry.entry_type === "break" || entry.entry_type === "hero") continue;
    const hours = Number(entry.hours_decimal);
    if (!Number.isFinite(hours) || hours <= 0) continue;
    const workDate = String(entry.work_date || "").trim();
    const position = employeeBookedPositionMeta(entry);
    rows.push({
      id: `emp:${entry.id}`,
      person: String(entry.user_display || entry.user_login || "").trim() || "Unbekannt",
      when: workDate || "—",
      whenSort: parseWhenSort(workDate ? `${workDate}T12:00:00` : null),
      hours,
      source: "employee",
      sourceLabel: "Zeiterfassung",
      comment: entry.comment ?? null,
      ...position,
    });
  }
  return rows;
}

export function sumBookedHoursRows(rows: BookedHoursRow[]): number {
  return rows.reduce((sum, row) => sum + row.hours, 0);
}

export function sortBookedHoursRows(rows: BookedHoursRow[]): BookedHoursRow[] {
  return [...rows].sort((a, b) => b.whenSort - a.whenSort || a.person.localeCompare(b.person, "de"));
}

export async function fetchOrderBookedHoursDetail(
  order: Order,
  options?: { includeEmployeeTimes?: boolean },
): Promise<BookedHoursDetail> {
  const orderId = order.id;
  const includeEmployeeTimes = Boolean(options?.includeEmployeeTimes);
  let heroError = false;
  let subcontractorError = false;
  let employeeError = false;
  let heroItems: HeroTimeEntry[] = [];
  let reassignedHeroItems: HeroTimeEntry[] = [];
  let subcontractorRows: BookedHoursRow[] = [];
  let employeeRows: BookedHoursRow[] = [];

  const heroProjectId = order.hero_project_id;
  const tasks: Promise<void>[] = [];

  if (heroProjectId) {
    tasks.push(
      getHeroTimes(Number(heroProjectId))
        .then((items) => {
          heroItems = items;
        })
        .catch((err) => {
          console.warn("[bookedHours] Hero-Zeiten laden fehlgeschlagen:", err);
          heroError = true;
        }),
    );
  }

  if (Number.isFinite(orderId) && orderId > 0) {
    tasks.push(
      listHeroStundenZuordnung(orderId).then((items) => {
        reassignedHeroItems = items;
      }),
    );
  }

  if (Number.isFinite(orderId) && orderId > 0) {
    tasks.push(
      getSubcontractorTimes(orderId)
        .then((items) => {
          subcontractorRows = buildSubcontractorRows(items);
        })
        .catch((err) => {
          console.warn("[bookedHours] Nachunternehmer-Zeiten laden fehlgeschlagen:", err);
          subcontractorError = true;
        }),
    );
    if (includeEmployeeTimes) {
      tasks.push(
        listEmployeeTimes({ auftrag_id: orderId })
          .then((items) => {
            employeeRows = buildEmployeeRows(items);
          })
          .catch((err) => {
            console.warn("[bookedHours] Eigene Zeiterfassung laden fehlgeschlagen:", err);
            employeeError = true;
          }),
      );
    }
  }

  await Promise.all(tasks);

  const heroRows = buildHeroRows(mergeHeroTimeItems(heroItems, reassignedHeroItems));
  const rows = sortBookedHoursRows([...heroRows, ...subcontractorRows, ...employeeRows]);
  const partialError =
    (heroError && heroProjectId != null) ||
    (subcontractorError && Number.isFinite(orderId)) ||
    (employeeError && includeEmployeeTimes);
  const totalHours = partialError && !rows.length ? 0 : sumBookedHoursRows(rows);

  return {
    totalHours,
    rows,
    heroError: Boolean(heroError && heroProjectId),
    subcontractorError,
    employeeError: includeEmployeeTimes ? employeeError : undefined,
  };
}

/** Gebuchte Stunden (Hero + NU + App-ZE) anhand der Auftrags-ID. */
export async function fetchAuftragBookedHoursDetail(auftragId: number): Promise<BookedHoursDetail> {
  const order = await getOrder(auftragId);
  return fetchOrderBookedHoursDetail(order, { includeEmployeeTimes: true });
}

export function formatBookedHoursWhen(value: string): string {
  if (!value || value === "—") return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) {
    const dateOnly = /^\d{4}-\d{2}-\d{2}$/.test(value);
    if (dateOnly) {
      const parts = value.split("-").map((p) => Number(p));
      const y = parts[0] ?? 0;
      const mo = parts[1] ?? 1;
      const day = parts[2] ?? 1;
      const local = new Date(y, mo - 1, day);
      return local.toLocaleDateString("de-DE", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      });
    }
    return value;
  }
  const hasTime = value.includes("T") || /\d{1,2}:\d{2}/.test(value);
  if (hasTime) {
    return d.toLocaleString("de-DE", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  }
  return d.toLocaleDateString("de-DE", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
}

export { formatHours };
